import { getMyPackages } from "../services/PackageService";
import type { CreditPurchase } from "../constants/packages";

export const PKG_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

let cached: { userId: string; packages: CreditPurchase[]; timestamp: number } | null = null;

/**
 * @param userId       Cache is scoped per user — different user always gets a fresh fetch.
 * @param ttlMs        How long cached data is considered fresh (default 24 hours).
 *                     Packages change infrequently so a long TTL is appropriate.
 * @param onBackground When provided and the cache is stale, stale data is returned
 *                     immediately and a background refresh runs (stale-while-revalidate).
 */
export async function getCachedPackages(
  userId: string,
  ttlMs = PKG_CACHE_TTL_MS,
  onBackground?: (packages: CreditPurchase[]) => void,
): Promise<{ packages: CreditPurchase[]; error?: string | null }> {
  if (cached && cached.userId === userId) {
    const isStale = Date.now() - cached.timestamp >= ttlMs;
    if (!isStale) {
      return { packages: cached.packages };
    }
    if (onBackground) {
      const stale = cached.packages;
      const snapshotTimestamp = cached.timestamp;
      getMyPackages()
        .then((result) => {
          if (result.data && Array.isArray(result.data)) {
            if (cached?.timestamp === snapshotTimestamp && cached?.userId === userId) {
              cached = { userId, packages: result.data, timestamp: Date.now() };
            }
            onBackground(result.data);
          }
        })
        .catch(() => {});
      return { packages: stale };
    }
  }

  const result = await getMyPackages();
  if (result.data && Array.isArray(result.data)) {
    cached = { userId, packages: result.data, timestamp: Date.now() };
  }
  return {
    packages: Array.isArray(result.data) ? result.data : [],
    error: result.error,
  };
}

export function invalidatePackageCache(): void {
  cached = null;
}
